/**
 * Route-level access rules shared between NavigationContext (sidebar
 * visibility) and the shell gate (RouteRoleGate).
 *
 * This is defense-in-depth only — the backend enforces RBAC via
 * checkPermission() on every API call. The route guards here just stop
 * users from landing on a page whose API calls would all 403 anyway.
 *
 * Source of truth for role → screen visibility. Keep in sync with the
 * backend permission matrix in packages/core/src/rbac/permissions.ts.
 *
 * A subject with an E1.5 grant (`managed`, #1336) is answered by its grant,
 * not its role: `decideScreen` below is the one decision every surface asks
 * (#1645). Without a grant (`legacy`) it is `canAccessScreen`, unchanged.
 */

import type { AccessMeResponse, AnalyticsCapability } from '@aica/api-types/schemas';
import { canWriteCoaching } from '@aica/core/rbac';
import {
  SIDEBAR_SECTIONS,
  getRouteFeatureFlag,
} from '@/components/layout/sidebar-config';
import type { FeatureFlags } from '@/contexts/FeatureFlagsContext';

export type GlobalRole = 'client' | 'admin' | 'agent' | 'supervisor';

/**
 * Screen name keys map to sidebar items and UI surfaces. Platform owners
 * (`isOwner: true`) see every screen regardless of this map.
 */
const SCREEN_ROLES = {
  dashboard: ['client', 'admin', 'agent', 'supervisor'],
  projects: ['client', 'admin', 'supervisor'],
  records: ['client', 'admin', 'agent', 'supervisor'],
  // The deal queue (#766): what a viewer sees is the server's boundary (the
  // deals of the conversations they may see), as for records.
  deals: ['client', 'admin', 'agent', 'supervisor'],
  alerts: ['client', 'admin', 'supervisor'],
  billing: ['client'],
  reports: ['client', 'admin'],
  'analysis-setup': ['client', 'admin'],
  settings: ['client', 'admin', 'agent', 'supervisor'],
  users: ['client', 'admin'],
  'call-stats': ['client', 'admin', 'agent', 'supervisor'],
  'missed-calls': ['client', 'admin', 'agent', 'supervisor'],
  // `agent.list` на сервере: оператору раздел не отдаётся (#848, спека §2.3).
  'voice-of-customer': ['client', 'admin', 'supervisor'],
  // The script steps report (#879): `aggregates.quality` is the team's data, an
  // employee has none of it — the API answers an `agent` without a grant 403.
  'script-steps': ['client', 'admin', 'supervisor'],
  // The criteria report on its own screen (E4, #1342; the report of #866):
  // `aggregates.quality`, the audience of the old dashboards' Criteria tab.
  'criteria-report': ['client', 'admin', 'supervisor'],
  'ai-feedback': ['client', 'admin', 'agent', 'supervisor'],
  'ai-tasks': ['client', 'admin', 'agent', 'supervisor'],
  competitions: ['client', 'admin', 'agent', 'supervisor'],
  'knowledge-base': ['client', 'admin', 'agent', 'supervisor'],
  // Every role: what a viewer may ask about is the server's boundary (the
  // records they may see), not the screen's (#765 Ф3).
  assistant: ['client', 'admin', 'agent', 'supervisor'],
  'embedded-b24': ['client', 'admin'],
  // `admin` belongs here because the backend grants it human-review
  // read/list/update ('human-review' in packages/core/src/rbac/permissions.ts).
  // Without it an org admin got no sidebar item and a bounced deep link while
  // every API call behind the screen would have answered 200.
  'review-queue': ['client', 'admin', 'supervisor'],
  coaching: ['supervisor'],
  // Pages outside the sidebar, open to every role: one employee's page (an
  // agent sees its own, the API answers who may see whom) and the objections
  // stream behind the Quality card. Listed so a grant holder's gate knows the
  // data they read (`MANAGED_SCREEN_RULES`); without a grant the page itself
  // answers, as before.
  'agent-profile': ['client', 'admin', 'agent', 'supervisor'],
  objections: ['client', 'admin', 'agent', 'supervisor'],
  // admin: platform-owner-only — enforced via isOwner, not via this map
} satisfies Record<string, readonly GlobalRole[]>;

/**
 * A screen of the role map. A name that is not one is a compile error where
 * a screen is written down (the path table below, the managed rules), so a
 * typo cannot fail open (review #1678).
 */
export type Screen = keyof typeof SCREEN_ROLES;

export const SCREEN_ACCESS: Readonly<Record<Screen, readonly GlobalRole[]>> =
  SCREEN_ROLES;

/** Is this name a screen of the role map. */
export function isScreen(name: string): name is Screen {
  return Object.prototype.hasOwnProperty.call(SCREEN_ACCESS, name);
}

export function canAccessScreen(
  screen: string,
  globalRole: GlobalRole | null,
  isOwner: boolean
): boolean {
  if (isOwner) return true;
  if (!globalRole) return false;
  if (!isScreen(screen)) return true; // unknown screen → permissive (handled by NotFound)
  return SCREEN_ACCESS[screen].includes(globalRole);
}

// ============================================================================
// E1.5 — screens of a grant holder (#1645)
// ============================================================================

/**
 * What a screen of a `managed` subject is decided by:
 *
 * - `capabilities` — the data the screen reads; every one must be in the
 *   grant (the API answers 403 `capability_required` otherwise, whatever the
 *   base role — reads are delegated, spec «Решения» 1–2). `roleMust` — a
 *   screen whose every read is the caller's own writes (coaching) shows
 *   nothing to a base role that may not write, whatever the grant;
 * - `role` — finance, settings, configuration and the placeholders with no
 *   data behind them stay with the role (decision 2): `SCREEN_ACCESS` decides,
 *   exactly as without a grant;
 * - `refused` — the API refuses the screen to every managed subject: the human
 *   review queue (no record predicate a grant could narrow it by) and the
 *   assistant about the whole base («Применение»).
 *
 * The grant's SCOPE decides no screen: it narrows the data inside one.
 */
export type ManagedScreenRule =
  | {
      kind: 'capabilities';
      needs: readonly AnalyticsCapability[];
      roleMust?: (role: GlobalRole) => boolean;
    }
  | { kind: 'role' }
  | { kind: 'refused' };

const ROLE: ManagedScreenRule = { kind: 'role' };
const REFUSED: ManagedScreenRule = { kind: 'refused' };
const needs = (...caps: AnalyticsCapability[]): ManagedScreenRule => ({
  kind: 'capabilities',
  needs: caps,
});

/**
 * One rule per `SCREEN_ACCESS` key (a test holds the two key sets equal).
 * Capabilities are the ones `CAPABILITY_ROUTES`
 * (apps/edge-api/src/middleware/require-capability.ts) requires of the
 * screen's primary read.
 */
export const MANAGED_SCREEN_RULES: Readonly<Record<Screen, ManagedScreenRule>> = {
  // GET /dashboard needs nothing: its projection keeps the grant's sections.
  dashboard: needs(),
  projects: ROLE,
  // GET /records
  records: needs('records.read'),
  // GET /deals — visibility follows the last conversation; same capability as
  // records (CAPABILITY_ROUTES `/deals*`).
  deals: needs('records.read'),
  // GET /alerts — read by grant; acting on an alert stays role ∧ grant.
  alerts: needs('aggregates.quality'),
  billing: ROLE,
  reports: ROLE,
  'analysis-setup': ROLE,
  settings: ROLE,
  users: ROLE,
  // GET /analytics/call-stats
  'call-stats': needs('aggregates.flow'),
  // The page is a list of records (GET /records).
  'missed-calls': needs('records.read'),
  // GET /analytics/voice
  'voice-of-customer': needs('aggregates.voc'),
  // GET /reports/script-steps, /reports/script-steps/:stepId
  'script-steps': needs('aggregates.quality'),
  // GET /reports/criteria, /reports/criteria/:criterionId
  'criteria-report': needs('aggregates.quality'),
  // Placeholders: no data behind the page yet.
  'ai-feedback': ROLE,
  'ai-tasks': ROLE,
  competitions: ROLE,
  'knowledge-base': ROLE,
  assistant: REFUSED,
  'embedded-b24': ROLE,
  'review-queue': REFUSED,
  // GET /agents/me/coaching/notes/sent, /agents/coaching/sessions — the notes
  // the caller SENT and the sessions it takes part in. Reading them is
  // `team.scores`, but only a role that may write coaching (role ∧ grant,
  // #1336) has any: for a group lead whose base role is `agent` the page would
  // be empty for good, so the screen is not offered.
  coaching: {
    kind: 'capabilities',
    needs: ['team.scores'],
    roleMust: (role) => canWriteCoaching(role),
  },
  // GET /agents/:agentId/performance, /agents/:agentId/coaching/*
  'agent-profile': needs('team.scores'),
  // GET /agents/objections, /agents/objections/stream
  objections: needs('aggregates.voc'),
};

/**
 * `/access/me` as a screen sees it:
 *
 * - `loading` — not answered yet: the ROLE decides, exactly as before E1.5;
 * - `resolved` — the answer: a grant narrows (and widens reads) from here on;
 * - `failed` — could not be read, or is not being asked (no organization or
 *   no session to ask with): the role decides.
 *
 * Why the role while unknown, and not "wait" or "nothing": every subject
 * without a grant — nearly everyone — would otherwise lose or wait for screens
 * it has always had. The cost lands on a grant holder only, and it is bounded:
 * for a moment it may see a screen its role opens and its grant does not.
 * That screen shows no data the grant does not cover — the API answers a
 * managed subject by its grant whatever the screen (403 `capability_required`,
 * a projection, or 503), never with the role's data. Once the answer says
 * `managed`, the gate leaves the screen and the navigation drops it.
 *
 * `failed` with `retry` (#1677) — the read was ASKED and failed with no answer
 * in hand, so nobody knows whether the subject holds a grant: the role decides
 * meanwhile, and the shell says so and offers to ask again
 * (`AccessReadFailedBanner`); `retry` settles when the read it started (or
 * joined) does. Without `retry` nothing was asked (no organization, no
 * session, no provider) — nothing is unknown, nothing to say.
 */
export type AccessState =
  | { status: 'loading' }
  | { status: 'resolved'; access: AccessMeResponse }
  | { status: 'failed'; retry?: () => Promise<void> };

export type ScreenDecision = 'allowed' | 'denied';

export interface ScreenSubject {
  globalRole: GlobalRole | null;
  isOwner: boolean;
  access: AccessState;
}

/** The grant, when the answer says there is one; `null` — the role decides. */
export function managedAccessOf(
  access: AccessState
): Extract<AccessMeResponse, { mode: 'managed' }> | null {
  return access.status === 'resolved' && access.access.mode === 'managed'
    ? access.access
    : null;
}

/**
 * «Can this subject open screen X» — the one decision RouteRoleGate, the
 * sidebar, the bottom nav, the command palette and the embedded tabs ask.
 *
 * - platform owner — every screen (always `legacy` on the server);
 * - no grant, `/access/me` still loading or failed — `canAccessScreen`, byte
 *   for byte (see `AccessState` for why the role while unknown);
 * - a grant — `MANAGED_SCREEN_RULES`.
 */
export function decideScreen(screen: string, subject: ScreenSubject): ScreenDecision {
  const byRole: ScreenDecision = canAccessScreen(
    screen,
    subject.globalRole,
    subject.isOwner
  )
    ? 'allowed'
    : 'denied';
  if (subject.isOwner || !subject.globalRole) return byRole;
  const rule = isScreen(screen) ? MANAGED_SCREEN_RULES[screen] : undefined;
  const managed = managedAccessOf(subject.access);
  // Unknown screens and role-ruled ones answer the same in both modes.
  if (!managed || !rule || rule.kind === 'role') return byRole;
  if (rule.kind === 'refused') return 'denied';
  if (rule.roleMust && !rule.roleMust(subject.globalRole)) return 'denied';
  return rule.needs.every((cap) => managed.capabilities.includes(cap))
    ? 'allowed'
    : 'denied';
}

// ============================================================================
// Which screen a page is, and which pages the gate decides (#1645)
// ============================================================================

/**
 * Pages a screen rule covers that have no sidebar item of their own. A page
 * that gets a sidebar item needs no line here: the sidebar's href is read.
 */
const OFF_SIDEBAR_SCREEN_ROUTES: ReadonlyArray<{ path: string; screen: Screen }> = [
  // /agents/$agentId — one employee's page (team.scores for a grant holder).
  { path: '/agents', screen: 'agent-profile' },
  // The objections stream behind the Quality card (aggregates.voc).
  { path: '/analytics/objections', screen: 'objections' },
  // The criteria report behind the Quality card (aggregates.quality).
  { path: '/analytics/criteria', screen: 'criteria-report' },
  // The old customer-questions address (#843, #844) redirects into Voice of
  // Customer: it is that screen, decided by the same roles wherever a path is
  // looked up. The redirect itself runs in the router before the shell gate
  // (the router context carries no role), so an agent lands on the target and
  // is refused there like any visit — sent to /dashboard; the target does not
  // redirect back, so there is no loop.
  { path: '/analytics/customer-questions', screen: 'voice-of-customer' },
];

/**
 * Path → screen: every sidebar item that names a screen of the role map
 * (`href` and `matchPatterns`, as `getRouteFeatureFlag` reads them), and the
 * pages above. `/admin/*` is not a screen — the shell gate decides it by
 * `isOwner`.
 */
const SCREEN_PATHS: ReadonlyArray<{ path: string; screen: Screen }> = [
  ...SIDEBAR_SECTIONS.flatMap((section) =>
    section.items.flatMap((item) => {
      const screen = item.id;
      return isScreen(screen)
        ? [...new Set([item.href, ...(item.matchPatterns ?? [])])].map((path) => ({
            path,
            screen,
          }))
        : [];
    })
  ),
  ...OFF_SIDEBAR_SCREEN_ROUTES,
];

/**
 * The screen a path belongs to, or `undefined` — ONE table for the portal's
 * shell gate and the Bitrix24 tabs. A second hand-kept prefix list had
 * already missed two grant-gated pages (review #1652); the embedded tabs kept
 * a third (review #1678). Exact path first, then the longest prefix, so a
 * nested page is never claimed by a shorter sibling.
 */
export function screenForPath(pathname: string): Screen | undefined {
  const exact = SCREEN_PATHS.find((route) => route.path === pathname);
  if (exact) return exact.screen;
  return [...SCREEN_PATHS]
    .sort((a, b) => b.path.length - a.path.length)
    .find((route) => pathname.startsWith(`${route.path}/`))?.screen;
}

/**
 * Screens the gate has always decided for every subject, by the role. The
 * rest of the role map is left to the page itself (every role that reaches it
 * is answered by the page) — except for a grant holder, whose grant may not
 * cover the data the page reads: a page of 403s is a dead end (#1645). Without
 * a grant they are not consulted — nothing changes.
 */
const ROLE_GATED_SCREENS: ReadonlySet<Screen> = new Set<Screen>([
  'review-queue',
  // Replaces the in-component visibleScreens check the old /prompt-builder
  // and /scripts pages carried before they became redirects into the hub.
  'analysis-setup',
  // «Голос клиента» (#848): оператору сервер отдаёт 403 — страница из одних
  // отказов ему не нужна.
  'voice-of-customer',
  // The script steps report (#879): an employee gets 403 on both reads — a page
  // of refusals is not offered.
  'script-steps',
  // The criteria report (E4, #1342): the same — both reads answer an employee 403.
  'criteria-report',
]);

/**
 * Does the gate decide this screen for this subject at all — the portal's
 * shell gate and the Bitrix24 tabs ask the same question. A screen it does
 * not decide opens whatever the role, as before E1.5; a role-ruled screen
 * answers a grant holder as it answers everyone.
 */
export function isGatedScreen(screen: Screen, access: AccessState): boolean {
  if (ROLE_GATED_SCREENS.has(screen)) return true;
  return managedAccessOf(access) !== null && MANAGED_SCREEN_RULES[screen].kind !== 'role';
}

/**
 * A screen the role refuses may still be the grant's (a group lead whose base
 * role is `agent` opening voice of customer): while `/access/me` is in
 * flight, wait for it instead of leaving — a redirect replaces the deep link,
 * and the answer could not bring it back. Only a screen a grant can OPEN
 * waits (its managed rule is `capabilities`); the rest are decided at once.
 */
export function waitsForAccess(screen: Screen, access: AccessState): boolean {
  return (
    access.status === 'loading' && MANAGED_SCREEN_RULES[screen].kind === 'capabilities'
  );
}

/**
 * May a link on screen lead to `path` (#1677) — the shell gate's own answer,
 * asked before the click instead of after it. A dashboard drill-down to a
 * screen the gate would bounce back to `/dashboard` is a dead end: it is not
 * offered.
 *
 * Only a grant holder is asked: without a grant (and while `/access/me` is
 * loading or failed) every link stays as it was — a role's dashboard links
 * only where its role goes. A grant holder is answered by `decideScreen` for
 * EVERY screen, the role-ruled ones too (`projects` for an analyst whose base
 * role is `agent`): the same answer the sidebar gives, so a dashboard never
 * links to a screen the navigation does not offer (code-review #1705). A path
 * that is no screen (a page the role map does not name) is the page's own
 * business, as at the gate.
 */
export function canOpenPath(
  path: string,
  subject: ScreenSubject | null,
  checks: LinkChecks = {}
): boolean {
  const pathname = path.split(/[?#]/, 1)[0] ?? path;
  // The route's feature flag first, as the layout does: `RouteFeatureGate`
  // sits outside the role gate and decides for every subject.
  if (checks.featureOff) {
    const flag = getRouteFeatureFlag(pathname);
    if (flag && checks.featureOff(flag)) return false;
  }
  const screen = screenForPath(pathname);
  if (!screen) return true;
  return canOpenScreen(screen, subject, checks);
}

/**
 * What a link asks beyond the default («only a grant holder is asked»):
 *
 * - `byRole` — a subject without a grant is answered by `decideScreen` too,
 *   for a surface that offers every capability to a role (the unified
 *   dashboard: a legacy client holds them all, yet `coaching` is a
 *   supervisor's screen);
 * - `featureOff` — is the flag `RouteFeatureGate` reads for this route
 *   (`getRouteFeatureFlag`) known to be off: such a link leads to the
 *   «feature not available» page. `useFeatureKnownOff` (components/ui/
 *   FeatureGate) is the one answer: the flag context's own verdict, so a flag a
 *   shell cannot read (the Bitrix24 session carries a subset) is not «off».
 */
export interface LinkChecks {
  byRole?: boolean;
  featureOff?: (flag: keyof FeatureFlags) => boolean;
}

/**
 * `canOpenPath` for a link whose screen is known: a page that takes a
 * parameter (`/agents/$agentId`) asks by its screen, not by a made-up path.
 * Same answer as the path form — only a grant holder is asked, unless
 * `checks.byRole`.
 */
export function canOpenScreen(
  screen: Screen,
  subject: ScreenSubject | null,
  checks: LinkChecks = {}
): boolean {
  if (!subject || subject.isOwner) return true;
  if (!checks.byRole && !managedAccessOf(subject.access)) return true;
  return decideScreen(screen, subject) === 'allowed';
}

/**
 * What a question to the assistant about ONE record needs of a grant —
 * `POST /assistant/ask` in `CAPABILITY_ROUTES`
 * (apps/edge-api/src/middleware/require-capability.ts): the record and its
 * text.
 */
const RECORD_ASSISTANT_CAPABILITIES: readonly AnalyticsCapability[] = [
  'records.read',
  'evidence.text',
];

/**
 * May the assistant drawer ask about the record on screen (#1677). Without a
 * grant — the role decides, as before; a grant must name what the ask route
 * requires, or every question is a 403.
 */
export function canAskAboutRecord(subject: ScreenSubject | null): boolean {
  if (!subject || subject.isOwner) return true;
  const managed = managedAccessOf(subject.access);
  if (!managed) return true;
  return RECORD_ASSISTANT_CAPABILITIES.every((cap) => managed.capabilities.includes(cap));
}

/**
 * Which role dashboard `/dashboard` (and the embedded overview) renders.
 * `null` — no role to render for (the caller discloses it).
 *
 * Until `/access/me` answers, the role's dashboard, at once — as before E1.5
 * (`AccessState`). It is replaced when the answer is a grant whose dashboard
 * differs. Every role's dashboard can differ under some grant (any member may
 * be given either preset), so waiting would mean a skeleton for everyone; the
 * role's dashboard costs a grant holder at most a moment of sections the API
 * answers by its grant.
 *
 * A grant holder is not shown the dashboard of its base role when that
 * dashboard reads what the grant does not cover:
 * - with `team.scores` and `aggregates.team` (the «Руководитель группы»
 *   preset) — the team dashboard: every read it makes is in the grant, and the
 *   API answers them for the grant's people whatever the base role (an `agent`
 *   base no longer gets its personal dashboard);
 * - without them, a `client`/`admin` base keeps its dashboard (finance there
 *   follows the role); an `agent`/`supervisor` base gets the admin overview —
 *   the personal and the team dashboards are built on employee scores the
 *   grant does not include, while the admin overview's one required read is
 *   `/dashboard`, which the API projects to the grant's sections, and each of
 *   its other reads degrades to an empty widget on its own.
 */
export function dashboardFor(
  role: GlobalRole | null,
  access: AccessState
): GlobalRole | null {
  if (!role) return null;
  const managed = managedAccessOf(access);
  if (!managed) return role;
  const caps = managed.capabilities;
  if (caps.includes('team.scores') && caps.includes('aggregates.team')) {
    return 'supervisor';
  }
  return role === 'client' || role === 'admin' ? role : 'admin';
}

/**
 * Admin-area routes (`/admin/*`) are gated behind `isOwner` only —
 * platform super-admins, not org roles.
 */
export function canAccessAdminArea(isOwner: boolean): boolean {
  return isOwner;
}
